import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Business,
  Language,
  Refresh,
  Group,
  AttachMoney,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Account } from '../services/crmService';

const AccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState<Partial<Account>>({
    name: '',
    type: 'Prospect',
    industry: '',
    annualRevenue: 0,
    numberOfEmployees: 0,
    website: '',
    phone: '',
    email: '',
    rating: 'Warm',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    status: 'Active',
    description: '',
  });

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await crmService.getAccounts();
      const accountsData = Array.isArray(response) ? response : 
                          response.accounts ? response.accounts : 
                          response.data ? response.data : [];
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setSnackbar({ open: true, message: 'Failed to fetch accounts', severity: 'error' });
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['annualRevenue', 'numberOfEmployees'].includes(name) ? Number(value) : value
    }));
  };

  // Handle dialog open/close
  const handleOpenDialog = (account?: Account) => {
    if (account) {
      setEditingAccount(account);
      setFormData(account);
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        type: 'Prospect',
        industry: '',
        annualRevenue: 0,
        numberOfEmployees: 0,
        website: '',
        phone: '',
        email: '',
        rating: 'Warm',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        status: 'Active',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
  };

  // Handle save (create/update)
  const handleSave = async () => {
    try {
      if (editingAccount) {
        await crmService.updateAccount(editingAccount._id!, formData);
        setSnackbar({ open: true, message: 'Account updated successfully', severity: 'success' });
      } else {
        await crmService.createAccount(formData as Omit<Account, '_id'>);
        setSnackbar({ open: true, message: 'Account created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      setSnackbar({ open: true, message: 'Failed to save account', severity: 'error' });
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await crmService.deleteAccount(id);
        setSnackbar({ open: true, message: 'Account deleted successfully', severity: 'success' });
        fetchAccounts();
      } catch (error) {
        console.error('Error deleting account:', error);
        setSnackbar({ open: true, message: 'Failed to delete account', severity: 'error' });
      }
    }
  };

  // Filter accounts based on search
  const filteredAccounts = accounts.filter(account =>
    `${account.name} ${account.industry} ${account.type}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
      'Active': 'success',
      'Inactive': 'error',
      'Pending': 'warning'
    };
    return colors[status] || 'default';
  };

  // Get type color
  const getTypeColor = (type: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default'> = {
      'Customer': 'success',
      'Prospect': 'info',
      'Partner': 'primary',
      'Vendor': 'secondary',
      'Competitor': 'warning',
      'Other': 'default'
    };
    return colors[type] || 'default';
  };

  // Get rating icon
  const getRatingIcon = (rating?: string) => {
    const icons = {
      'Hot': '🔥',
      'Warm': '☀️',
      'Cold': '❄️'
    };
    return rating ? icons[rating as keyof typeof icons] || '' : '';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate statistics
  const stats = {
    total: accounts.length,
    customers: accounts.filter(a => a.type === 'Customer').length,
    totalRevenue: accounts.reduce((sum, a) => sum + (a.annualRevenue || 0), 0),
    activeAccounts: accounts.filter(a => a.status === 'Active').length,
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Accounts</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Group sx={{ color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.customers}</Typography>
                    <Typography variant="body2" color="text.secondary">Customers</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{formatCurrency(stats.totalRevenue)}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.activeAccounts}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Accounts</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, mr: 2 }}>
            <TextField
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 400 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAccounts}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              New Account
            </Button>
          </Box>
        </Box>

        {/* Accounts Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Revenue</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={8}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No accounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((account) => (
                      <TableRow key={account._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {account.name}
                            </Typography>
                            {account.website && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Language fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {account.website}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.type}
                            color={getTypeColor(account.type)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{account.industry || '-'}</TableCell>
                        <TableCell>
                          {account.annualRevenue ? formatCurrency(account.annualRevenue) : '-'}
                        </TableCell>
                        <TableCell>{account.numberOfEmployees || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={account.status}
                            color={getStatusColor(account.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {account.rating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{getRatingIcon(account.rating)}</span>
                              {account.rating}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(account)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(account._id!)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAccounts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingAccount ? 'Edit Account' : 'Create New Account'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                >
                  <MenuItem value="Prospect">Prospect</MenuItem>
                  <MenuItem value="Customer">Customer</MenuItem>
                  <MenuItem value="Partner">Partner</MenuItem>
                  <MenuItem value="Vendor">Vendor</MenuItem>
                  <MenuItem value="Competitor">Competitor</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Rating"
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                >
                  <MenuItem value="Hot">🔥 Hot</MenuItem>
                  <MenuItem value="Warm">☀️ Warm</MenuItem>
                  <MenuItem value="Cold">❄️ Cold</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Annual Revenue"
                  name="annualRevenue"
                  type="number"
                  value={formData.annualRevenue}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Employees"
                  name="numberOfEmployees"
                  type="number"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Address Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              {editingAccount ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default AccountsPage;